import React, { useState, useEffect } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import {
    AccessTimeRounded,
    CalendarTodayRounded,
    CloseTwoTone,
    EmojiPeopleRounded,
    MenuTwoTone,
    PersonRounded,
    WbSunnyRounded,
    NightsStayRounded,
    FavoriteBorderRounded,
    ShoppingCartRounded,
} from '@mui/icons-material';
import { Tooltip, Box, IconButton, Chip, keyframes, Badge } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import OptionsMenu from './Optionsmenu';
import { useDateContext } from '../../context/DateContext';
import { useThemeMode } from '../../context/ThemeModeContext';
import { getTodayEnglishDate, getTodayNepaliDate } from '../../utils/dateConverter';
import BreadcrumbsPath from './BreadcrumbsPath';
import { getAuthData } from '../../helper/getAuthData';
import { useNavigate } from 'react-router-dom';
import { useGetAllCartProducts } from '../../apis/cartAPI/CartAPI';
import { useGetWishListProductData } from '../../apis/wishlistAPI/WishlistAPI';

// Animation for shimmer effect
const shimmer = keyframes`
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(100%);
    }
`;

const InfoChip = ({ label, icon, color = 'primary' }) => {
    const theme = useTheme();
    return (
        <Tooltip title={label} arrow>
            <Chip
                size="small"
                icon={icon}
                label={label}
                sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    height: 34,
                    borderRadius: theme.shape.borderRadius,
                    background: alpha(theme.palette[color].main, 0.08),
                    color: theme.palette[color].main,
                    border: `1px solid ${alpha(theme.palette[color].main, 0.15)}`,
                    backdropFilter: 'blur(4px)',
                    '& .MuiChip-icon': {
                        fontSize: 16,
                        color: 'inherit',
                        ml: 0.5,
                    },
                    '& .MuiChip-label': {
                        px: 1.5,
                    },
                    '&:hover': {
                        background: alpha(theme.palette[color].main, 0.15),
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s ease',
                        boxShadow: `0 4px 12px ${alpha(theme.palette[color].main, 0.2)}`,
                    },
                }}
            />
        </Tooltip>
    );
};

export default function DesktopHeader({ onToggleSidebar, sidebarOpen }) {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { useNepaliDate } = useDateContext();
    const { mode, toggleThemeMode } = useThemeMode();
    const { data: cartData } = useGetAllCartProducts();
    const cartCount = Array.isArray(cartData) ? cartData.length : 0;
    const { data: wishlistData } = useGetWishListProductData();
    const wishlistCount = Array.isArray(wishlistData?.ProductList) ? wishlistData.ProductList.length : Array.isArray(wishlistData?.WishList) ? wishlistData.WishList.length : Array.isArray(wishlistData) ? wishlistData.length : 0;
    const [currentTime, setCurrentTime] = useState(dayjs());

    useEffect(() => {
        const interval = setInterval(() => setCurrentTime(dayjs()), 1000);
        return () => clearInterval(interval);
    }, []);

    const formatTimeWithAMPM = () => useNepaliDate ? currentTime.format('HH:mm:ss') : currentTime.format('hh:mm:ss A');
    const currentDate = useNepaliDate ? getTodayNepaliDate() : getTodayEnglishDate();
    const getGreeting = () => {
        const hour = currentTime.hour();
        if (hour < 12) return t('header.goodMorning');
        if (hour < 17) return t('header.goodAfternoon');
        return t('header.goodEvening');
    };
    const authData = getAuthData();

    // Get greeting icon based on time of day
    const getGreetingIcon = () => {
        const hour = currentTime.hour();
        if (hour < 12) return <WbSunnyRounded sx={{ fontSize: 16 }} />;
        if (hour < 17) return <WbSunnyRounded sx={{ fontSize: 16 }} />;
        return <NightsStayRounded sx={{ fontSize: 16 }} />;
    };

    return (
        <Box
            sx={{
                display: { xs: 'none', md: 'block' },
                width: '100%',
                position: 'sticky',
                top: 0,
                p: 0.5,
                zIndex: 1100,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
                backdropFilter: 'blur(12px)',
                borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `radial-gradient(circle at 0% 0%, ${alpha(theme.palette.primary.main, 0.03)} 0%, transparent 50%)`,
                    pointerEvents: 'none',
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.05)}, transparent)`,
                    // animation: `${shimmer} 3s infinite`,
                    pointerEvents: 'none',
                },
            }}
        >
            <Stack
                direction="row"
                sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 1.5,
                    maxWidth: '1700px',
                    mx: 'auto',
                    position: 'relative',
                    zIndex: 1,
                }}
                spacing={2}
            >
                <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
                    <IconButton
                        onClick={onToggleSidebar}
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: theme.shape.borderRadius,
                            background: alpha(theme.palette.primary.main, 0.08),
                            color: theme.palette.primary.main,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                            transition: 'all 0.2s ease',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                width: 0,
                                height: 0,
                                borderRadius: '50%',
                                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                transform: 'translate(-50%, -50%)',
                                transition: 'width 0.6s, height 0.6s',
                            },
                            '&:hover': {
                                background: alpha(theme.palette.primary.main, 0.15),
                                transform: 'scale(1.05)',
                                '&::before': {
                                    width: 100,
                                    height: 100,
                                },
                            },
                        }}
                    >
                        {sidebarOpen ? <CloseTwoTone /> : <MenuTwoTone />}
                    </IconButton>
                    <BreadcrumbsPath />
                </Stack>

                <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
                    <InfoChip label={currentDate} icon={<CalendarTodayRounded sx={{ fontSize: 16 }} />} />
                    <InfoChip label={formatTimeWithAMPM()} icon={<AccessTimeRounded sx={{ fontSize: 16 }} />} />
                    <InfoChip
                        label={`${getGreeting()}, ${authData?.FirstName?.split(' ')[0] || t('header.userProfile')}!`}
                        icon={getGreetingIcon()}
                    />
                    <>
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <Tooltip title={t('nav.wishlist')}>
                                <IconButton size="small" onClick={() => navigate('/wishlist')}>
                                <Badge badgeContent={wishlistCount} color="error" invisible={wishlistCount === 0} sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16 } }}>
                                        <FavoriteBorderRounded sx={{ fontSize: 20 }} />
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Tooltip title={t('nav.cart')}>
                            <IconButton size="small" onClick={() => navigate('/cart')}>
                                <Badge badgeContent={cartCount} color="error" invisible={cartCount === 0} sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16 } }}>
                                    <ShoppingCartRounded sx={{ fontSize: 20 }} />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                    </>

                    <Tooltip title={mode === 'dark' ? t('header.lightMode') : t('header.darkMode')} arrow>
                        <IconButton
                            onClick={toggleThemeMode}
                            size="small"
                            sx={{
                                width: 34,
                                height: 34,
                                borderRadius: theme.shape.borderRadius,
                                background: alpha(theme.palette.primary.main, 0.08),
                                color: theme.palette.primary.main,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    background: alpha(theme.palette.primary.main, 0.15),
                                    transform: 'scale(1.05)',
                                },
                            }}
                        >
                            {mode === 'dark' ? <WbSunnyRounded sx={{ fontSize: 16 }} /> : <NightsStayRounded sx={{ fontSize: 16 }} />}
                        </IconButton>
                    </Tooltip>
                    <OptionsMenu />
                </Stack>
            </Stack>

            {/* Subtle bottom accent line */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 2,
                    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.5)}, transparent)`,
                }}
            />
        </Box>
    );
}
